import { authenticationStore } from "@/state/auth/AuthStore";
import {
  requestCreateModelTransaction,
  requestUpdateModelTransaction,
  requestUploadModelTransactionAttachments,
  requestUploadModelTransactionFiles,
} from "@/state/transactionModels/TransactionModelEffects";
import { PostModelCreationRequestDto } from "@/state/transactionModels/request/post.model.transaction.request.dto";
import { useCurrentTransaction } from "@/state/transactions/CurrentTransactionHook";
import { AuthenticationContext } from "@/type/core/auth/auth.types";
import { FileHelper } from "@/utils/helpers/file-helper";
import { toastError } from "@/utils/helpers/toast-helper";
import Accordions from "@components/core/accordions/Accordions";
import BasicButton from "@components/core/buttons/BasicButton";
import PageLayout from "@components/core/layouts/PageLayout";
import { ROUTES } from "@routes/Routes";
import ModelNameSection from "@views/core/extranet/menu/offices/transactionModels/details/sections/ModelNameSection";
import AttachmentsSection from "@views/core/extranet/transactions/signature/sections/AttachmentsSection";
import FilesToSignSection from "@views/core/extranet/transactions/signature/sections/FilesToSignSection";
import SignatoriesSection from "@views/core/extranet/transactions/signature/sections/SignatoriesSection/SignatoriesSection";
import SignatureMessageSection from "@views/core/extranet/transactions/signature/sections/SignatureMessageSection";
import { useUnit } from "effector-react";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Section from "@components/core/section/Section";
import { setTransactionSignatures } from "@/state/transactions/CurrentTransactionEvents";

const TransactionModelScreen: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    transactionType,
    transactionForm,
    files,
    attachments,
    signatories,
    signatures,
    isFromDraft,
  } = useCurrentTransaction();

  const currentFileCount = files.length + attachments.length;
  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);

  const office = authStore?.user?.officesForCgu.find(
    (office) => office.id === transactionForm.office,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeKey, setActiveKey] = useState<string[]>([
    "modelOfficeSelected",
    "modelFilesToSignSelection",
    ...(transactionType && transactionType === "signature"
      ? ["modelsAttachmentsSelection", "modelSignatories"]
      : []),
    "modelParameters",
  ]);

  const officeSelectedName = office && office.alias;
  const [signaturePositioning, setSignaturePositioning] = useState<boolean>(
    signatures.length > 0,
  );

  useEffect(() => {
    if (!files.length) {
      setTransactionSignatures([]);
      setSignaturePositioning(false);
    }
  }, [files]);

  const uploadAttachments = (transactionId: string) => {
    const attachmentBody = new FormData();
    attachments.forEach((attachment, key) => {
      if (!attachment.file) {
        return;
      }
      attachmentBody?.append("files[]", attachment.file);
      attachmentBody?.append("documentOrders[]", (key + 1).toString());
    });

    requestUploadModelTransactionAttachments({
      dto: attachmentBody,
      id: transactionId,
    })
      .then((response) => {
        if (!response.ok) {
          toastError(t("error.toast.model.attachmentsUpload"));
        }
      })
      .catch(() => {
        toastError(t("error.toast.model.attachmentsUpload"));
      })
      .finally(() => setIsLoading(false));
  };

  const uploadModelDocuments = (transactionId: string) => {
    const uploadingAttachments: boolean = attachments.length > 0;
    const uploadingFiles: boolean = files.length > 0;

    let attachmentBody: FormData | undefined = undefined;
    let filesBody: FormData | undefined = undefined;

    if (uploadingAttachments) {
      attachmentBody = new FormData();
      attachments.forEach((attachment, key) => {
        if (!attachment.file) {
          return;
        }
        attachmentBody?.append("files[]", attachment.file);
        attachmentBody?.append("documentOrders[]", (key + 1).toString());
      });
    }

    if (uploadingFiles) {
      filesBody = FileHelper.createUploadFilesFormData({
        transactionType,
        files,
        signatories,
        signatures,
        signatureRequirementMode: "ALL",
      });
    }

    Promise.all([
      uploadingAttachments && attachmentBody
        ? requestUploadModelTransactionAttachments({
            dto: attachmentBody,
            id: transactionId,
          })
        : [],
      uploadingFiles && filesBody
        ? requestUploadModelTransactionFiles({
            dto: filesBody,
            id: transactionId,
          })
        : [],
    ])
      .then((responses) => {
        const [attachmentsResponse, filesResponse] = responses;
        const attachmentsResponseExists =
          !Array.isArray(attachmentsResponse) &&
          attachmentsResponse.ok !== undefined;
        const filesResponseExists =
          !Array.isArray(filesResponse) && filesResponse.ok !== undefined;

        if (
          (!attachmentsResponseExists || attachmentsResponse.ok) &&
          (!filesResponseExists || filesResponse.ok)
        ) {
          navigate(
            ROUTES.extranet.menu.offices.transactionModel.list.generate(),
          );
        }

        if (attachmentsResponseExists && !attachmentsResponse.ok) {
          toastError(t("error.toast.model.attachmentsUpload"));
        }
        if (filesResponseExists && !filesResponse.ok) {
          toastError(t("error.toast.model.filesUpload"));
        }
      })
      .catch(() => {
        toastError(t("error.toast.model.documentsUpload"));
      })
      .finally(() => setIsLoading(false));
  };

  const handlePositioning = () => {
    navigate(
      ROUTES.extranet.menu.offices.transactionModel.positioning.generate(),
    );
  };

  const handleSave = () => {
    setIsLoading(true);

    const modelTransaction: PostModelCreationRequestDto = {
      name: transactionForm.name,
      transactionType: transactionType,
      signatories,
      invitationMode:
        transactionType === "signature"
          ? transactionForm.invitationMode
            ? transactionForm.invitationMode
            : null
          : null,
      officeId: transactionForm.office,
      reference: transactionForm.reference,
      extraRecipients: transactionForm.extraRecipients ?? [],
      isConfidential: transactionForm.confidential ?? false,
      documents: files.length > 0 ? undefined : [], // on passe par le uploadFiles si on a des documents
      signatures: signatures.length > 0 ? undefined : [],
      attachments: attachments.length > 0 ? undefined : [], // on passe par le uploadFiles si on a des attachments
    };

    const request = isFromDraft
      ? requestUpdateModelTransaction
      : requestCreateModelTransaction;
    request({
      dto: modelTransaction,
      id: isFromDraft ? transactionForm.id : undefined,
    })
      .then((response) => {
        if (response.ok && response.data && response.data.id) {
          setIsLoading(false);
          if (files.length < 1 && attachments.length < 1) {
            navigate(
              ROUTES.extranet.menu.offices.transactionModel.list.generate(),
            );
          } else {
            if (signaturePositioning) {
              if (attachments && attachments.length > 0) {
                uploadAttachments(response.data.id);
              }
              handlePositioning();
            } else {
              uploadModelDocuments(response.data.id);
            }
          }
        } else {
          toastError(t("error.toast.model.transactionsCreate"));
          setIsLoading(false);
        }
      })
      .catch(() => {
        toastError(t("error.toast.model.transactionsCreate"));
        setIsLoading(false);
      });
  };

  const handleSubmitSection = (key: string) => {
    setActiveKey(activeKey.filter((value) => value !== key));
  };

  return (
    <PageLayout
      container
      returnRoute={ROUTES.extranet.menu.offices.transactionModel.list.generate()}
      contentInCard
      isLoading={isLoading}
    >
      <>
        <Section className="pt-15">
          <Accordions
            activeKey={activeKey}
            onChange={(keys) => {
              if (Array.isArray(keys)) {
                setActiveKey(keys);
              } else {
                setActiveKey([keys]);
              }
            }}
            sections={[
              {
                key: "modelOfficeSelected",
                title:
                  t("offices.transactionModel.office.title") +
                  officeSelectedName,
                content: (
                  <ModelNameSection
                    onSubmit={() => handleSubmitSection("modelOfficeSelected")}
                    name={transactionForm.name}
                  />
                ),
              },
              {
                key: "modelFilesToSignSelection",
                title: t("offices.transactionModel.filesToSign.title"),
                content: (
                  <FilesToSignSection
                    isFromModel
                    isSignature={transactionType === "signature"}
                    description={t(
                      "signature.accordion.documentForm.description",
                    )}
                    descriptionSeparator={t(
                      "signature.accordion.documentForm.descriptionSeparator",
                    )}
                    descriptionUrlTitle={t(
                      "signature.accordion.documentForm.descriptionUrlTitle",
                    )}
                    signaturePositioning={signaturePositioning}
                    setSignaturePositioning={setSignaturePositioning}
                    onSubmit={() =>
                      handleSubmitSection("modelFilesToSignSelection")
                    }
                    currentFileCount={currentFileCount}
                  />
                ),
              },
              ...(transactionType && transactionType === "signature"
                ? [
                    {
                      key: "modelsAttachmentsSelection",
                      title: t(
                        "offices.transactionModel.filesAttachment.title",
                      ),
                      content: (
                        <AttachmentsSection
                          attachments={attachments}
                          isFromModel
                          description={t(
                            "signature.accordion.attachmentForm.description",
                          )}
                          descriptionSeparator={t(
                            "signature.accordion.attachmentForm.descriptionSeparator",
                          )}
                          descriptionUrlTitle={t(
                            "signature.accordion.attachmentForm.descriptionUrlTitle",
                          )}
                          onSubmit={() =>
                            handleSubmitSection("modelsAttachmentsSelection")
                          }
                          currentFileCount={currentFileCount}
                        />
                      ),
                    },
                    {
                      key: "modelSignatories",
                      title: t("offices.transactionModel.signatories.title"),
                      content: (
                        <SignatoriesSection
                          me={authStore?.user}
                          isFromModel
                          onSubmit={() =>
                            handleSubmitSection("modelSignatories")
                          }
                          isFromDraft={isFromDraft}
                          signatures={signatures}
                        />
                      ),
                    },
                  ]
                : []),
              {
                key: "modelParameters",
                title: t("offices.transactionModel.parameters.title"),
                content: (
                  <SignatureMessageSection isFromModel officeId={office?.id} />
                ),
              },
            ]}
          />
        </Section>
        <div className="mt-4 d-flex align-items-center justify-content-center justify-content-md-end">
          <BasicButton
            text={
              signaturePositioning
                ? t(`offices.transactionModel.preposition.${transactionType}`)
                : t("buttons.save")
            }
            disabled={
              isLoading ||
              transactionForm.name == undefined ||
              transactionForm.name.length < 1
            }
            isLoading={isLoading}
            className="mw-160"
            onClick={handleSave}
          />
        </div>
      </>
    </PageLayout>
  );
};

export default TransactionModelScreen;
